'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import ScriptureCard from './ScriptureCard';
import GratitudeMessage from './GratitudeMessage';
import ElderWisdomCard from './ElderWisdomCard';
import PrayerRequestForm from './PrayerRequestForm';
import CulturalTraditionCard from './CulturalTraditionCard';
import ShareScriptureButton from './ShareScriptureButton';

interface Scripture {
  verse: string;
  reference: string;
  theme: string;
}

interface GratitudeMsg {
  name: string;
  message: string;
  date: string;
  relationship: string;
}

interface ElderWisdom {
  name: string;
  image: string;
  alt: string;
  yearsMarried: number;
  wisdom: string;
  role: string;
}

interface CulturalTradition {
  title: string;
  description: string;
  significance: string;
  icon: string;
}

interface PrayerFormData {
  name: string;
  email: string;
  prayer: string;
  anonymous: boolean;
}

const FaithGratitudeInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState<'scripture' | 'gratitude' | 'wisdom' | 'traditions'>('scripture');
  const [prayerCount, setPrayerCount] = useState(47);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const scriptures: Scripture[] = [
  {
    verse: "Love is patient, love is kind. It does not envy, it does not boast, it is not proud. It does not dishonor others, it is not self-seeking, it is not easily angered, it keeps no record of wrongs.",
    reference: "1 Corinthians 13:4-5",
    theme: "The Nature of Love"
  },
  {
    verse: "Therefore what God has joined together, let no one separate.",
    reference: "Mark 10:9",
    theme: "Sacred Covenant"
  },
  {
    verse: "Two are better than one, because they have a good return for their labor: If either of them falls down, one can help the other up.",
    reference: "Ecclesiastes 4:9-10",
    theme: "Partnership & Unity"
  },
  {
    verse: "Above all, love each other deeply, because love covers over a multitude of sins.",
    reference: "1 Peter 4:8",
    theme: "Enduring Love"
  }];


  const gratitudeMessages: GratitudeMsg[] = [
  {
    name: "Sarah Johnson",
    message: "Watching you both grow in faith and love has been a blessing to our entire family. Your commitment to God and each other inspires us all.",
    date: "Jan 20, 2026",
    relationship: "Family Friend"
  },
  {
    name: "Pastor Michael Deng",
    message: "It has been an honor to witness your spiritual journey together. May God continue to guide your steps and bless your union abundantly.",
    date: "Jan 18, 2026",
    relationship: "Church Pastor"
  },
  {
    name: "Rebecca & David Mabior",
    message: "Your love story is a testament to God's perfect timing. We are grateful to be part of your celebration and pray for many blessed years ahead.",
    date: "Jan 15, 2026",
    relationship: "Community Members"
  },
  {
    name: "Auntie Grace Nyandeng",
    message: "Seeing you honor our traditions while building your future fills my heart with joy. May your home be filled with love, laughter, and God's presence.",
    date: "Jan 12, 2026",
    relationship: "Extended Family"
  }];


  const elderWisdom: ElderWisdom[] = [
  {
    name: "Elder John & Mary Garang",
    image: "/assets/images/M_G-1783666867743.jpg",
    alt: "Elderly African couple in traditional attire smiling warmly at camera in home setting",
    yearsMarried: 42,
    wisdom: "Marriage is not about finding the perfect person, but learning to see an imperfect person perfectly through God's eyes. Pray together daily, forgive quickly, and never go to bed angry.",
    role: "Community Elders"
  },
  {
    name: "Pastor & Mrs. Samuel Dut",
    image: "/assets/images/14-1783667475005.jpeg",
    alt: "Middle-aged African couple in formal church attire standing together with warm expressions",
    yearsMarried: 35,
    wisdom: "The secret to a lasting marriage is putting God first, your spouse second, and yourself third. When you both seek to serve rather than be served, love flourishes naturally.",
    role: "Church Leaders"
  },
  {
    name: "Uncle Peter & Auntie Rose Makuei",
    image: "/assets/images/M_G-1783666867743.jpg",
    alt: "Smiling elderly African couple in traditional South Sudanese attire with warm expressions outdoors",
    yearsMarried: 38,
    wisdom: "Communication is the bridge between two hearts. Never let the sun set on your anger, and always remember why you fell in love in the first place.",
    role: "Family Elders"
  }];


  const culturalTraditions: CulturalTradition[] = [
  {
    title: "Dowry (Bride Price) Tradition",
    description: "In South Sudanese culture, the dowry represents a sacred covenant between families, symbolizing respect, commitment, and the joining of two communities. It traditionally includes cattle, gifts, and financial contributions that honor the bride's family.",
    significance: "This practice strengthens family bonds, demonstrates the groom's commitment and ability to provide, and creates lasting connections between extended families that support the marriage throughout life.",
    icon: "GiftIcon"
  },
  {
    title: "Community Blessing Ceremony",
    description: "Before the wedding, elders from both families gather to offer prayers, wisdom, and blessings over the couple. This ceremony invokes God's favor and seeks ancestral approval for the union.",
    significance: "Community involvement ensures the couple enters marriage with spiritual covering, practical guidance, and a support network that extends beyond the nuclear family.",
    icon: "UserGroupIcon"
  },
  {
    title: "Traditional Wedding Attire",
    description: "The bride and groom wear specially crafted traditional garments that reflect South Sudanese heritage, often featuring vibrant colors, intricate beadwork, and symbolic patterns representing their tribes and families.",
    significance: "Traditional attire honors cultural identity, connects the couple to their ancestors, and visually celebrates the rich heritage being passed to future generations.",
    icon: "SparklesIcon"
  },
  {
    title: "Unity Dance & Celebration",
    description: "Wedding celebrations feature traditional dances, songs, and music that tell the story of the couple's journey. Family members and community participate in joyful expressions of support and celebration.",
    significance: "These celebrations create lasting memories, strengthen community bonds, and mark the transition into married life with joy, music, and collective blessing.",
    icon: "MusicalNoteIcon"
  }];


  const handlePrayerSubmit = (data: PrayerFormData) => {
    setPrayerCount((prev) => prev + 1);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="animate-pulse space-y-8 p-8">
          <div className="h-64 bg-surface rounded-lg" />
          <div className="h-96 bg-surface rounded-lg" />
          <div className="h-64 bg-surface rounded-lg" />
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6">
            <Icon name="HeartIcon" size={40} className="text-primary" variant="solid" />
          </div>
          <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            Faith & Gratitude
          </h1>
          <p className="font-inter text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Our union is built on the foundation of faith in God and gratitude for the community that surrounds us. We celebrate the spiritual journey that brought us together and honor the traditions that guide our path forward.
          </p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="sticky top-16 z-40 bg-card border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex overflow-x-auto scrollbar-hide">
            {[
            { id: 'scripture', label: 'Scripture', icon: 'BookOpenIcon' },
            { id: 'gratitude', label: 'Gratitude Wall', icon: 'HeartIcon' },
            { id: 'wisdom', label: 'Elder Wisdom', icon: 'AcademicCapIcon' },
            { id: 'traditions', label: 'Our Traditions', icon: 'SparklesIcon' }].
            map((tab) =>
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center space-x-2 px-6 py-4 font-inter text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-200 ${
              activeTab === tab.id ?
              'border-primary text-primary' : 'border-transparent text-muted hover:text-foreground hover:border-border'}`
              }>

                <Icon name={tab.icon as any} size={18} />
                <span>{tab.label}</span>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Scripture Section */}
      {activeTab === 'scripture' &&
      <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-playfair text-3xl md:text-4xl font-semibold text-foreground mb-4">
                Guiding Scriptures
              </h2>
              <p className="font-inter text-base text-muted-foreground max-w-2xl mx-auto">
                These verses illuminate our path and remind us of God's design for marriage
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {scriptures.map((scripture, index) =>
            <div key={index} className="space-y-4">
                  <ScriptureCard
                verse={scripture.verse}
                reference={scripture.reference}
                theme={scripture.theme} />

                  <div className="flex justify-end">
                    <ShareScriptureButton
                  verse={scripture.verse}
                  reference={scripture.reference} />

                  </div>
                </div>
            )}
            </div>

            {/* Prayer Request Section */}
            <div className="max-w-3xl mx-auto mt-16">
              <div className="text-center mb-8">
                <h3 className="font-playfair text-2xl md:text-3xl font-semibold text-foreground mb-3">
                  Prayer Requests
                </h3>
                <p className="font-inter text-base text-muted-foreground">
                  Share your prayer needs with our community. We believe in the power of collective prayer.
                </p>
                <div className="flex items-center justify-center space-x-2 mt-4">
                  <Icon name="HandRaisedIcon" size={20} className="text-primary" />
                  <span className="font-inter text-sm font-medium text-primary">
                    {prayerCount} prayers submitted
                  </span>
                </div>
              </div>
              <PrayerRequestForm onSubmit={handlePrayerSubmit} />
            </div>
          </div>
        </section>
      }

      {/* Gratitude Wall Section */}
      {activeTab === 'gratitude' &&
      <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-playfair text-3xl md:text-4xl font-semibold text-foreground mb-4">
                Gratitude Wall
              </h2>
              <p className="font-inter text-base text-muted-foreground max-w-2xl mx-auto">
                Messages of appreciation from our beloved community
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {gratitudeMessages.map((message, index) =>
            <GratitudeMessage
              key={index}
              name={message.name}
              message={message.message}
              date={message.date}
              relationship={message.relationship} />

            )}
            </div>

            <div className="mt-12 text-center">
              <div className="inline-flex items-center space-x-2 px-6 py-3 bg-primary/10 rounded-full">
                <Icon name="HeartIcon" size={20} className="text-primary" variant="solid" />
                <span className="font-inter text-sm font-medium text-primary">
                  We are grateful for each blessing shared
                </span>
              </div>
            </div>
          </div>
        </section>
      }

      {/* Elder Wisdom Section */}
      {activeTab === 'wisdom' &&
      <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-playfair text-3xl md:text-4xl font-semibold text-foreground mb-4">
                Wisdom from Our Elders
              </h2>
              <p className="font-inter text-base text-muted-foreground max-w-2xl mx-auto">
                Timeless advice from couples who have walked the path of marriage with grace and faith
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {elderWisdom.map((elder, index) =>
            <ElderWisdomCard
              key={index}
              name={elder.name}
              image={elder.image}
              alt={elder.alt}
              yearsMarried={elder.yearsMarried}
              wisdom={elder.wisdom}
              role={elder.role} />

            )}
            </div>

            <div className="mt-16 max-w-3xl mx-auto bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg p-8 border border-border">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="LightBulbIcon" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-playfair text-xl font-semibold text-foreground mb-3">
                    Collective Wisdom
                  </h3>
                  <p className="font-inter text-sm text-foreground leading-relaxed">
                    "Marriage is a journey of two imperfect people learning to love perfectly through God's grace. Honor each other, communicate openly, pray together daily, and never forget that your union is a testimony of God's faithfulness. Let patience, forgiveness, and gratitude be the pillars of your home."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      }

      {/* Cultural Traditions Section */}
      {activeTab === 'traditions' &&
      <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-playfair text-3xl md:text-4xl font-semibold text-foreground mb-4">
                Our Cultural Traditions
              </h2>
              <p className="font-inter text-base text-muted-foreground max-w-2xl mx-auto">
                Honoring South Sudanese heritage through sacred customs and meaningful practices
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {culturalTraditions.map((tradition, index) =>
            <CulturalTraditionCard
              key={index}
              title={tradition.title}
              description={tradition.description}
              significance={tradition.significance}
              icon={tradition.icon} />

            )}
            </div>

            <div className="mt-16 max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-secondary/10 to-primary/10 rounded-lg p-8 md:p-12 border border-border">
                <div className="text-center">
                  <Icon name="SparklesIcon" size={48} className="text-primary mx-auto mb-6" variant="solid" />
                  <h3 className="font-playfair text-2xl md:text-3xl font-semibold text-foreground mb-4">
                    Bridging Heritage & Faith
                  </h3>
                  <p className="font-inter text-base text-foreground leading-relaxed mb-6">
                    Our wedding celebrates the beautiful intersection of South Sudanese cultural traditions and Christian faith. We honor the wisdom of our ancestors while building our foundation on biblical principles. Each tradition carries deep meaning, connecting us to our roots while pointing us toward our future together.
                  </p>
                  <div className="flex flex-wrap justify-center gap-4">
                    <div className="flex items-center space-x-2 px-4 py-2 bg-card rounded-full">
                      <Icon name="HeartIcon" size={18} className="text-primary" variant="solid" />
                      <span className="font-inter text-sm font-medium text-foreground">Cultural Pride</span>
                    </div>
                    <div className="flex items-center space-x-2 px-4 py-2 bg-card rounded-full">
                      <Icon name="BookOpenIcon" size={18} className="text-secondary" />
                      <span className="font-inter text-sm font-medium text-foreground">Biblical Foundation</span>
                    </div>
                    <div className="flex items-center space-x-2 px-4 py-2 bg-card rounded-full">
                      <Icon name="UserGroupIcon" size={18} className="text-accent" />
                      <span className="font-inter text-sm font-medium text-foreground">Community Unity</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      }

      {/* Closing Scripture */}
      <section className="py-16 px-4 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="max-w-4xl mx-auto text-center">
          <Icon name="BookOpenIcon" size={48} className="text-primary mx-auto mb-6" />
          <blockquote className="font-playfair text-2xl md:text-3xl font-semibold text-foreground italic mb-4">
            "And now these three remain: faith, hope and love. But the greatest of these is love."
          </blockquote>
          <p className="font-inter text-base text-muted-foreground">
            1 Corinthians 13:13
          </p>
        </div>
      </section>
    </div>);

};

export default FaithGratitudeInteractive;